// import { storageLocal } from '/@/utils/storage'
import { notification as antMessage } from 'antd'
// import { usePermissionStoreHook } from '/@/store/modules/permission'

import Axios, {
  AxiosRequestConfig,
  AxiosInstance,
  AxiosResponse,
  CancelTokenSource
} from 'axios'

import Cookies from 'js-cookie'
import type { HttpConfig, Response, Interceptor } from './types.d'
import { isArray, isFunction, isObject, isString, merge } from 'lodash'
import { DefaultHttpConfig } from './const'
import { HttpResponse } from '@/types/http'
import { getToken } from '@/global.config'
import { LoginUrl } from '@/service/login/api/login'
import { useNavigate } from 'react-router-dom'
import { logout } from '@/pages/layout'
import { encryptData, decryptData, generateKey, sha256Encrypt } from '../crypto'
import CryptoJS from 'crypto-js'
import { isLlmbotNext, isSameOriginGateway } from '@/next/runtime.mjs'

// 类型定义好像有点问题，参考service
// const sso_url = import.meta.env.VITE_SSO_URL

const noopReject = error => Promise.reject(error)

interface InterceptorOption<T = any> {
  runwhen?(data: T): boolean | Promise<boolean>
}
type _Interceptor<T = any, D = any, R = AxiosResponse<T, D>> = [
  resolve: Interceptor<T, D, R>,
  reject: Interceptor<any, any>,
  options?: InterceptorOption<R> | undefined
]

class Interceptors {
  private interceptors: _Interceptor<any, any, any>[] = []

  use<T = any, D = any, R = AxiosResponse<T>>(
    resolve: Interceptor<T, D, R>,
    reject?: Interceptor<any, any>,
    options?: InterceptorOption<R>
  ) {
    this.interceptors.push([resolve, reject || noopReject, options])
  }

  private compose<T = any, D = any, R = AxiosResponse<T, D>>(
    interceptors: _Interceptor[],
    httpConfig: HttpConfig<T, D, R>
  ) {
    function runInterceptor(interceptor: _Interceptor) {
      const [resolve, reject, options] = interceptor
      async function runwhen(data) {
        const { runwhen } = options || {}
        if (isFunction(runwhen)) {
          return await runwhen(data)
        }
        return true
      }
      return async (prevInterData: any | Promise<any>) => {
        try {
          prevInterData = await prevInterData
          if (await runwhen(prevInterData))
            // @ts-ignore
            return resolve(prevInterData, httpConfig)
        } catch (error: any) {
          // @ts-ignore
          if (await runwhen(error)) return reject(error, httpConfig)
        }
      }
    }

    if (!interceptors?.length) return data => data
    if (interceptors.length === 1) {
      return data => runInterceptor(interceptors[0])(data)
    }
    return async data => {
      return interceptors.reduce(async (prevPromise, interceptor) => {
        return runInterceptor(interceptor)(prevPromise)
      }, Promise.resolve(data))
    }
  }

  run<T = any, D = any, R = AxiosResponse<T, D>>(
    data: any,
    config: HttpConfig<T, D, R>
  ) {
    return this.compose<T, D, R>([...this.interceptors], config)(data)
  }
}

class EnclosureHttp {
  // 保存当前Axios实例对象
  axiosInstance: AxiosInstance

  private activeRequests: Response<any, any>[] = []
  private config: HttpConfig | null = null

  private responseInterceptors = new Interceptors()
  private requestInterceptors = new Interceptors()

  constructor(config?: HttpConfig) {
    this.axiosInstance = Axios.create({
      // adapter: ['fetch', 'xhr', 'http'],
      baseURL: config?.baseUrl ?? '',
      withCredentials: true,
      timeout: 60000, //60秒超时
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    // this.axiosInstance.request()
    this.config = merge({}, config, DefaultHttpConfig)
    if (config?.requestInterceptor)
      this.requestInterceptors.use(config.requestInterceptor)
    if (config?.responseInterceptor)
      this.responseInterceptors.use(config.responseInterceptor)
    this.customInterceptor()
  }

  /**
   * @description 拦截相应
   * @returns void 0
   */
  private customInterceptor(): void {
    // instance.interceptors.request.use
    // this.responseInterceptors.use<HttpResponse>(
    //   (response, config: HttpConfig) => {

    //     return response
    //   },
    //   (error: any, config: HttpConfig) => {

    //   }
    // )

    this.responseInterceptors.use<HttpResponse>(
      (response, config: HttpConfig) => {

        // 检查是否是 SSE 响应
        const contentType = response.headers['content-type'] || '';

        // 检查是否是加密响应
        const isEncrypted = response.headers['x-forward-to'] === '1';
        const signed = response.headers['x-forward-for'];

        // 如果是普通加密响应，进行解密
        if (isEncrypted && signed && response.status === 200) {
          try {
            const AesKey = generateKey();

            // 确保response.config.data是字符串类型
            if (typeof response.data === 'string') {
              // 先解密数据
              const decryptedStr = decryptData(AesKey, response.data);

              // 如果decrypted是字符串，转换为UTF-8 bytes

              const decryptedBytes = CryptoJS.enc.Utf8.parse(decryptedStr);

              // 对解密后的原始数据进行签名验证
              const calculatedSign = sha256Encrypt(decryptedStr);

              if (calculatedSign !== signed) {
                // 签名不匹配，保持原始数据
                response.status = 400
                return response;
              }

              // 尝试解析为JSON
              let decryptedData: any;
              try {
                decryptedData = JSON.parse(decryptedStr);
              } catch {
                decryptedData = decryptedStr;
              }

              response.data = decryptedData;
            } else {
              console.log('response.config.data不是字符串类型，跳过解密');
            }
          } catch (error) {
            // 解密失败，保持原始数据
            console.warn('Response decryption failed:', error);
          }
        }

        let { checked = true } = this.getConfig(config),
          valid = true
        if (isFunction(checked)) {
          valid = checked(response)
        } else if (checked) {
          valid = response.data?.code === 0
        }
        if (!valid) {
          return Promise.reject(response)
        }

        return response
      },
      (error: any, config: HttpConfig) => {
        // 所有的响应异常 区分来源为取消请求/非取消请求
        // if (
        //   config.url === 'https://mmdialogue.airudder.com/api/record' ||
        //   config.url ===
        //     'https://livedemo-mmdialogue.airudder.com/api/record' ||
        //   config.url === 'https://mmdialogue-test.airudder.com/api/record' ||
        //   config.url === 'https://uat-mmdialogue.airudder.com/api/record'
        // ) {
        //   return Promise.resolve(data)
        // }
        if (
          error?.status == 401 &&
          error?.request?.responseURL?.indexOf(LoginUrl) == -1
        ) {
          // 退出
          logout()
          return
        }
        const { message = true } = this.getConfig(config)
        if (isFunction(message)) {
          message(error, antMessage)
        } else if (message) {
          let message = ''
          if (isString(error?.response?.data)) {
            message = error?.response?.data
          } else if (isObject(error?.response?.data)) {
            const detail = error?.response?.data?.detail
            if (isString(detail)) {
              message = detail
            } else {
              message = detail?.[0]?.msg
            }
          }
          antMessage.error({
            message:
              message ||
              error?.response?.statusText ||
              error?.message ||
              'unknown error'
          })
        }
        return Promise.reject(error)
      }
      // {
      //   runwhen(data) {
      //     // return this..requestConfig?.runWhen?.(data)
      //   },
      // }
    )
    this.requestInterceptors.use(
      (config: AxiosRequestConfig, httpConfig: HttpConfig) => {
        // if ($config.isMedusa) {
        //   // $config.baseURL = import.meta.env.VITE_MEDUSA_URL
        //   $config.headers['Authorization'] =
        //     storageLocal.getItem('Authorization')
        // }
        // if ($config.isBadCase) {
        //   $config.headers['dmrobot-token'] = $config.data?.dmrobot_token
        // }
        // console.log(config.url)
        const accses_token = getToken()
        if (accses_token)
          if (config.headers)
            config.headers['Authorization'] = 'Bearer ' + accses_token
          else
            config.headers = {
              Authorization: 'Bearer ' + accses_token
            }

        // 是否开关加密
        // return config;

        // 针对特定接口不进行加密
        const disabledEncryptPaths = [
          '/api/robot/template',
          '/api/robot-vs/detail',
          '/download',
        ]
        const isFormData = config.data instanceof FormData
        const isChatbotApi = this.axiosInstance.defaults.baseURL?.includes('/chatbot-api') || config.baseURL?.includes('/chatbot-api') || config.url?.includes('/chatbot-api')
        // The Next gateway validates plaintext fields inside HTTPS and supplies
        // upstream identity itself. Legacy encryption hides filters/body from it.
        const isNextGateway = isLlmbotNext(process.env) &&
          isSameOriginGateway(this.axiosInstance.getUri(config), window.location.origin)
        const shouldEncrypt = !isNextGateway && !isFormData && !isChatbotApi && !disabledEncryptPaths.some(disabledPath =>
          config.url?.includes(disabledPath)
        )

        if (!shouldEncrypt) {
          if (config.headers) {
            delete config.headers['X-Forward-To']
            delete config.headers['X-Forward-For']
          }
          return config
        }

        // 参考 business-web 的加密逻辑
        const AesKey = generateKey()
        let encryptStr: string | null = null


        // GET | DELETE 请求加密参数
        if ((config.method?.toLowerCase() === 'get' || config.method?.toLowerCase() === 'delete') || config.params) {
          let urls: string[] = [];
          const params = config.params || {};

          // 提取URL中已存在的查询参数
          if (config.url && config.url.includes('?')) {
            const urlParams = new URLSearchParams(config.url.split('?')[1]);
            for (const [key, value] of urlParams.entries()) {
              if (!(key in params)) { // config.params优先级更高
                urls.push(`${key}=${value}`);
              }
            }
          }

          // 添加config.params中的参数（优先级更高）
          for (const key in params) {
            if (params[key] !== undefined && params[key] !== null) {
              urls.push(`${key}=${params[key]}`);
            }
          }

          const uri = urls.join('&');

          if (config.headers) {
            config.headers["X-Forward-For"] = sha256Encrypt(uri);
          }

          encryptStr = encryptData(AesKey, uri, 'GET');

          // 构建新URL，避免重复问号
          if (config.url) {
            const baseUrl = config.url.split('?')[0];
            config.url = baseUrl + '?' + encodeURIComponent(encryptStr);
          }

          // 清空params，因为参数已经加密到URL中
          config.params = {};
        } else if (config.method?.toLowerCase() === 'post') {
          // POST 请求加密数据
          if (config.data === undefined) config.data = {};
          if (config.headers) {
            config.headers["X-Forward-For"] = sha256Encrypt(JSON.stringify(config.data));
          }
          encryptStr = encryptData(AesKey, config.data, 'POST');
          config.data = encryptStr;
        } else if (config.method?.toLowerCase() === 'put') {
          // PUT 请求加密数据
          if (config.data === undefined) config.data = {};
          if (config.headers) {
            config.headers["X-Forward-For"] = sha256Encrypt(JSON.stringify(config.data));
          }
          encryptStr = encryptData(AesKey, config.data, 'PUT');
          config.data = encryptStr;
        }
        if (config.headers) {
          config.headers["X-Forward-To"] = '1';
        }

        return config
      }
    )
  }

  private async _request<T = any, D = any, R = AxiosResponse<T>>(
    config: AxiosRequestConfig<D>,
    httpConfig: HttpConfig<T, D, R>,
    isRetry: boolean
  ) {
    let source = Axios.CancelToken.source()
    let { retryWhen, beforeRequest, afterRequest } = this.getConfig<T, D, R>(
      httpConfig || {}
    )
    if (isFunction(beforeRequest)) {
      beforeRequest({
        isRetry,
        cancelSource: source
      })
    }
    const _config = await this.requestInterceptors.run<T, D, R>(
      config,
      httpConfig
    )
    const request = this.axiosInstance
      .request<T, R, D>(
        merge({}, _config, {
          cancelToken: source.token
        })
      )
      .then(data => {
        if (isFunction(afterRequest)) {
          afterRequest(data, isRetry)
        }
        return data
      })
      .catch(async error => {
        if (isFunction(afterRequest)) {
          afterRequest(error, isRetry)
        }
        if (isFunction(retryWhen)) {
          return Promise.resolve(retryWhen(error))
            .then(needRetry => {
              if (needRetry)
                return this._request<T, D, R>(config, httpConfig, true)
              return Promise.reject(error)
            })
            .catch(retryError => {
              console.error(retryError)
              return Promise.reject(error)
            })
        }
        return Promise.reject(error)
      })
    return request
      .then(data => {
        return this.responseInterceptors.run<T, D, R>(data, httpConfig)
      })
      .catch(error => {
        return this.responseInterceptors.run<T, D, R>(
          Promise.reject(error),
          httpConfig
        )
      })
  }

  private getConfig<T = any, D = any, R = AxiosResponse<T, D>>(
    config: HttpConfig<T, D, R>
  ): HttpConfig<T, D, R> {
    return merge({}, config, this.config || {})
  }
  public request<T = any, D = any, R = AxiosResponse<T, D>>(
    config: AxiosRequestConfig<D> & {
      requestConfig?: HttpConfig<T, D, R>
    }
  ): Response<T, D, R> {
    const { requestConfig, ...restConfig } = config
    let { retryWhen, retryMax = DefaultHttpConfig.retryMax } = this.getConfig<
      T,
      D,
      R
    >(requestConfig || {})
    let cancelSource: CancelTokenSource | null = null
    const request = this._request<T, D, R>(
      restConfig,
      merge({}, this.config, requestConfig, {
        beforeRequest: data => {
          cancelSource = data.cancelSource
        },
        retryWhen(error: R | any) {
          if (isFunction(retryWhen)) {
            return Promise.resolve(retryWhen(error)).then(needRetry => {
              if (needRetry) {
                ; (<number>retryMax)--
                if (<number>retryMax <= 0) {
                  console.warn('retry is over max')
                  return false
                }
              }
              return needRetry
            })
          }
          return false
        }
      } as HttpConfig<T, D, R>),
      false
    )

    request
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        const index = this.activeRequests.indexOf(request)
        if (index > -1) this.activeRequests.splice(index, 1)
      })
    request.cancel = () => {
      cancelSource?.cancel()
    }
    this.activeRequests.push(request)
    return request
  }
}

export default EnclosureHttp
